import { GlobalStyles } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

interface CustomSunEditorProps {
  value: string;
  onChange: (content: string) => void;
  getSunEditorInstance?: (editor: any) => void;
  isPortrait?: boolean;
  isForNotification?: boolean;
}

const CustomSunEditor: React.FC<CustomSunEditorProps> = ({
  value,
  onChange,
  getSunEditorInstance,
  isPortrait = true,
  isForNotification = false,
}) => {
  const theme = useTheme();

  const previewTemplateForNotification = isForNotification
    ? `
  <div class="preview" style="margin-left: auto; margin-right: auto; width: 600px; border: dashed 1px; padding: inherit;">
	  {{contents}}
  </div>
  `
    : "";

  const pageWidth = isPortrait ? "210mm" : "297mm";
  const pageHeight = isPortrait ? "297mm" : "210mm";
  const pageLimit = isPortrait ? "245mm" : "175mm";

  const sunEditorStyles = {
    ".sun-editor .se-wrapper .se-wrapper-code": {
      height: isPortrait ? "297mm" : "210mm",
      minHeight: isPortrait ? "297mm" : "210mm",
    },
    ".sun-editor .se-wrapper .se-wrapper-wysiwyg": {
      width: isPortrait ? "210mm" : "297mm",
      maxWidth: "100%",

      minHeight: pageHeight,
      height: "auto",
      maxHeight: "none",

      margin: "auto",

      backgroundColor: "#fff",
      overflow: "visible",
    },
    ".sun-editor .se-wrapper .se-wrapper-wysiwyg::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundImage: `repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent calc(${pageLimit} - 2px),
    rgba(255, 0, 0, 0.75) calc(${pageLimit} - 2px),
    rgba(255, 0, 0, 0.75) ${pageLimit}
  )`,
      WebkitMaskImage: `repeating-linear-gradient(
    to right,
    #000 0, #000 8px,
    transparent 8px, transparent 14px
  )`,
      maskImage: `repeating-linear-gradient(
    to right,
    #000 0, #000 8px,
    transparent 8px, transparent 14px
  )`,
    },

    ".sun-editor .se-toolbar": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-tooltip": {
      color: theme.palette.text.primary,
    },
    ".sun-editor .se-dialog .se-dialog-inner": {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -25%)",
    },
    ".sun-editor .se-controller-resizing": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-list-layer": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor .se-controller .se-btn-group button": {
      backgroundColor: theme.palette.background.paper,
    },
    ".sun-editor button": {
      color: theme.palette.text.primary,
    },
  };
  return (
    <>
      <GlobalStyles styles={sunEditorStyles} />
      <SunEditor
        getSunEditorInstance={getSunEditorInstance}
        setContents={value}
        onChange={onChange}
        setOptions={{
          buttonList: [
            ["undo", "redo"],
            ["font", "fontSize"],
            ["paragraphStyle", "blockquote"],
            [
              "bold",
              "underline",
              "italic",
              "strike",
              "subscript",
              "superscript",
            ],
            ["fontColor", "hiliteColor"],
            ["align", "list", "lineHeight"],
            ["outdent", "indent"],

            ["table", "horizontalRule", "link", "image", "video"],
            // ['math'] //You must add the 'katex' library at options to use the 'math' plugin.
            // ['imageGallery'], // You must add the "imageGalleryUrl".
            ["showBlocks", "codeView"],
            //["preview", "print"],
            ["removeFormat"],
            isForNotification ? ["preview"] : [],
            // ['save', 'template'],
            // '/', Line break
          ], // Or Array of button list, eg. [['font', 'align'], ['image']]
          defaultTag: "div",
          minWidth: isForNotification ? "auto" : pageWidth,
          minHeight: pageHeight,
          height: "auto",
          maxHeight: "none",
          showPathLabel: false,
          attributesWhitelist: {
            all: "style",
            table: "width|height|align|cellpadding|cellspacing|border",
            input: "checked",
          },
          resizeEnable: false,
          linkNoPrefix: true,
          charCounter: isForNotification,
          strictHTMLValidation: isForNotification ? false : true,
          previewTemplate: previewTemplateForNotification,
        }}
        width={isForNotification ? "auto" : pageWidth}
        height="auto"
      />
    </>
  );
};

export default CustomSunEditor;
